import React, { useEffect, useState } from "react";
import styles from "./VendorManagement.module.css";
import WebService from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import { FaEye, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import AddVendorPanel from "./AddVendorPanel";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useSelector } from "react-redux";
import { RootState } from "../Services/Store"; 
import { AccessItem } from "../Models/Models";

interface Vendor {
  user_id: number;
  vendor_id: number;
  username: string;
  email: string;
  mobile_number: string;
  user_status: string;
  company_name: string;
  gst_number: string;
  address: {
    complete_address?: string;
    address?: string;
    landmark?: string;
  }[];
  category_ids: number[];
  raw_materials?: { category_id: number; category_name: string }[]; 
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

const VendorManagement: React.FC = () => {
  // RBAC: Get user access for this module
  const accessList = useSelector((state: RootState) => state.auth.access || []);
const moduleAccess = (accessList.find((item: any) => item.code === "vendor_management") as AccessItem) || {} as AccessItem;

const hasWriteAccess = moduleAccess.write === true;
const hasUpdateAccess = moduleAccess.update === true;
const hasDeleteAccess = moduleAccess["delete"] === true;


  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    page_size: 10,
    total_records: 0,
    total_pages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  // const [vendorType, setVendorType] = useState("All vendor");
  const [loading, setLoading] = useState(false);
  const [showAddVendorPanel, setShowAddVendorPanel] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const allChecked = vendors.length > 0 && selected.length === vendors.length;

  const navigate = useNavigate();

  // Fetch vendors from API
  const fetchVendors = async (page = 1) => {
    setLoading(true);
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/vendors?page_number=${page}&limit=${pagination.page_size}&name=${searchTerm}`,
      });
      if (res.status_code === 200 && res.data) {
        setVendors(res.data.vendors || []);
        setPagination(res.data.pagination);
        setSelected([]); // Reset selection on page change
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors(pagination.current_page);
    // eslint-disable-next-line
  }, [pagination.current_page]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    fetchVendors(pagination.current_page);
    // eslint-disable-next-line
  }, [searchTerm]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  // Select all/none
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelected(vendors.map(v => v.user_id));
    else setSelected([]);
  };

  // Select one row
  const handleSelectOne = (user_id: number) => {
    setSelected((prev) =>
      prev.includes(user_id)
        ? prev.filter((id) => id !== user_id)
        : [...prev, user_id]
    );
  };

  // DELETE Vendor (Single) - swirl alert
  const handleDeleteVendor = async (user_id: number) => {
    const vendor = vendors.find(v => v.user_id === user_id);
    const res = await Swal.fire({
      title: "Are you sure?",
      html: `<b>Delete vendor "${vendor?.username}"?</b><br>This cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ffc107",
      cancelButtonColor: "#ccc",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!res.isConfirmed) return;
    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/vendors/${user_id}`,
      });
      toast.success("Vendor deleted.");
      fetchVendors(pagination.current_page);
    } catch (e) {
      // toast.error("Failed to delete vendor.");
    }
  };

  // BULK Delete - swirl alert
  const handleBulkDelete = async () => {
    if (selected.length === 0) return toast.info("Select vendors to delete.");
    const res = await Swal.fire({
      title: "Are you sure?",
      html: `Delete <b>${selected.length}</b> vendor(s)?<br>This cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ffc107",
      cancelButtonColor: "#ccc",
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!res.isConfirmed) return;
    try {
      for (const user_id of selected) {
        await WebService.deleteAPI({ action: `/v1/door-management/vendors/${user_id}` });
      }
      toast.success("Selected vendors deleted.");
      fetchVendors(pagination.current_page);
    } catch (e) {
      // toast.error("Failed to delete one or more vendors.");
    }
  };

  // Status pill
  const statusPill = (status: string) => (
    <span
      className={
        status === "ACTIVE" ? styles.statusActive : styles.statusInactive
      }
      style={{
        display: "inline-block",
        minWidth: 60,
        textAlign: "center",
        borderRadius: 12,
        padding: "2px 10px",
        fontWeight: 500,
        fontSize: "0.97rem",
        background: status === "ACTIVE" ? "#e8f7e2" : "#f8e2e2",
        color: status === "ACTIVE" ? "#23953c" : "#e43e2d",
      }}
    >
      {status === "ACTIVE" ? "Active" : "Inactive"}
    </span>
  );

  // Actions (View, Edit, Delete)
const actionIcons = (vendor: Vendor) => (
  <span className={styles.actionIcons}>
    <FaEye
      title="View"
      className={styles.actionView}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/vendor-management/${vendor.vendor_id || vendor.user_id}`);
      }}
    />
    {/* RBAC: Only show Edit if user has update access */}
    {hasUpdateAccess && (
      <FaEdit
        title="Edit"
        className={styles.actionEdit}
        onClick={() => {
          setEditingVendor(vendor);
          setShowAddVendorPanel(true);
        }}
      />
    )}
    {/* RBAC: Only show Delete if user has delete access */}
    {hasDeleteAccess && (
      <FaTrash
        title="Delete"
        className={styles.actionDelete}
        onClick={() => handleDeleteVendor(vendor.user_id)}
      />
    )}
  </span>
);


  // Handle closing the add/edit panel
  const handleClosePanel = () => {
    setShowAddVendorPanel(false);
    setEditingVendor(null);
  };

  return (
    <div className={styles.container}>
      {/* --- Top Row: search/filter + Add vendor --- */}
      <div className={styles.topRow}>
        <div className={styles.leftFilters}>
          <input
            className={styles.searchBar}
            placeholder="Search vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ textAlign: "center" }}
          />
          {/* <select
            className={styles.filterDropdown}
            value={vendorType}
            onChange={(e) => setVendorType(e.target.value)}
            style={{ minWidth: 120, textAlign: "center" }}
          >
            <option value="All vendor">All vendor</option>
          </select> */}
        </div>
        {hasWriteAccess && (
          <button
            className={styles.addBtn}
            onClick={() => {
              setEditingVendor(null);
              setShowAddVendorPanel(true);
            }}
          >
            <FaPlus /> Add New Vendor
          </button>
        )}
      </div>

      {/* --- Select all row and bulk actions --- */}
      <div className={styles.selectAllRow} style={{ justifyContent: "center" }}>
        <label style={{ fontWeight: 500 }}>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={handleSelectAll}
            style={{ marginRight: 6 }}
          />
          Select all vendors
        </label>
        {/* RBAC: Only show Bulk Delete if user has delete access */}
        {hasDeleteAccess && (
          <button
            className={styles.bulkDeleteBtn}
            style={{
              marginLeft: 18,
              padding: "5px 14px",
              background: selected.length === 0 ? "#e0e0e0" : "#e43e2d",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontWeight: 500,
              cursor: selected.length === 0 ? "not-allowed" : "pointer",
              opacity: selected.length === 0 ? 0.6 : 1,
              fontSize: "1rem",
            }}
            disabled={selected.length === 0}
            onClick={handleBulkDelete}
          >
            <FaTrash style={{ marginRight: 6 }} /> Delete
          </button>
        )}
      </div>

      {/* --- Table --- */}
      <div className={styles.tableWrapper}>
        <table className={styles.vendorTable}>
          <thead>
            <tr>
              <th style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ textAlign: "center" }}>Vendor Name</th>
              <th style={{ textAlign: "center" }}>Phone</th>
              <th style={{ textAlign: "center" }}>GST Number</th>
              <th style={{ textAlign: "center" }}>Materials</th>
              <th style={{ textAlign: "center" }}>Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className={styles.loading} style={{ textAlign: "center", padding: "40px 0" }}>
                  Loading...
                </td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.noResults} style={{ textAlign: "center", padding: "40px 0" }}>
                  No vendors found.
                </td>
              </tr>
            ) : (
              vendors.map((vendor, i) => (
                <tr
                  key={vendor.user_id}
                  className={i % 2 === 1 ? styles.zebra : undefined}
                  style={{ textAlign: "center" }}
                >
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selected.includes(vendor.user_id)}
                      onChange={() => handleSelectOne(vendor.user_id)}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{ fontWeight: 700 }}>{vendor.username || "-"}</span>
                    <div className={styles.vendorCompany} style={{ color: "#888", fontSize: "0.98em", fontWeight: 400 }}>
                      {vendor.company_name || "-"}
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>{vendor.mobile_number || "-"}</td>
                  <td style={{ textAlign: "center" }}>{vendor.gst_number || "-"}</td>
                 <td style={{ textAlign: "center" }}>
  {Array.isArray(vendor.raw_materials)
    ? vendor.raw_materials.length
    : 0}
</td>

                  <td style={{ textAlign: "center" }}>{statusPill(vendor.user_status)}</td>
                  <td style={{ textAlign: "center" }}>{actionIcons(vendor)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination --- */}
      <div className={styles.paginationRow}>
        <Paginations
          totalItems={pagination.total_records}
          itemsCountPerPage={pagination.page_size}
          changePage={handlePageChange}
          changeCurrentPage={pagination.current_page}
        />
      </div>

      {/* --- Slide-in Add/Edit Vendor Panel --- */}
      {showAddVendorPanel && (
        <AddVendorPanel
          onClose={handleClosePanel}
          onSuccess={() => {
            handleClosePanel();
            fetchVendors(1);
          }}
          editVendor={editingVendor}
        />
      )}
    </div>
  );
};

export default VendorManagement;
